import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { clearSelectedLead } from "src/redux/actions/experiences/leadsAndReports";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import cx from "classnames";

import Slider from "src/components/slider";
import Button from "src/components/button";
import Reveal from "src/components/animation";
import { Input } from "src/components/input";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import { useCloneExperiencesStoryboard } from "src/query/experiences/storyboards";
import {
  selectDomains,
  selectStoryboard,
  selectStoryboardDraft,
  selectStoryboardIsDirty,
  selectStoryboardLoading,
} from "src/redux/selectors/experiences/storyboardSettings";

import { useWebsiteSettings } from "./hooks/useWebsiteSettings";
import ActionBar from "./components/actionBar";
import Tabs, { StoryboardSettingsTab } from "./components/tabs";
import SiteSettings from "./components/siteSettings";
import SiteSettingsSkeleton from "./components/siteSettings/skeleton";
import { canEdit, canManage } from "./utils/permissions";
import { toastFailure, toastSuccess } from "./utils/notifications";
import {
  isInteractiveContent,
  isSlideStoryboard,
  mapServerErrorToToast,
  validateAlias,
  validateGatings,
  validateName,
} from "./utils/validation";

import SaveConfirmation from "./components/saveConfirmation";
import styles from "./storyboardSettings.module.scss";
import { StoryboardCard } from "../storyboards/components/storyboardCard";
import Text from "src/components/text";
import { StoryboardItem } from "src/types/experiences/storyboards";
import { buildCleverstoryUrl, buildPagePreviewPath, buildPageTargetPath } from "../utils/cleverstoryRedirect";
import { STORYBOARD_TYPES } from "../types";

const APPLICATION_URL = process.env.REACT_APP_CLEVERSTORY_APP_URL;

const getActiveTab = (pathname: string): StoryboardSettingsTab =>
  pathname.includes("/leads") || pathname.includes("/reports")
    ? "leadsAndReports"
    // : pathname.includes("/permissions")
    // ? "rolesPermissions"
    : pathname.includes("/content-manager")
    ? "contentManager"
    : pathname.includes("/custom-fields")
    ? "customFields"
    : "siteSettings";

    interface ShellContextValue {
      setHideActionBar: (hide: boolean) => void;
      registerUploadHandler: (fn: (() => void) | null) => void;
      triggerUpload: () => void;
      registerDeleteHandler: (fn: (() => void) | null) => void;
    }

    export const ShellContext = React.createContext<ShellContextValue>({
      setHideActionBar: () => {},
      registerUploadHandler: () => {},
      triggerUpload: () => {},
      registerDeleteHandler: () => {},
    });

export const StoryboardShellLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { workspaceId, storyboardId } = useParams();

  const settings = useWebsiteSettings({ storyboardId: storyboardId ?? "" });
  const storyboard = useSelector(selectStoryboard);
  const draft = useSelector(selectStoryboardDraft);
  const isDirty = useSelector(selectStoryboardIsDirty);
  const fetchedDomains = useSelector(selectDomains);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [hideActionBar, setHideActionBar] = useState(false);
  const uploadHandlerRef = useRef<(() => void) | null>(null);
  const deleteHandlerRef = useRef<(() => void) | null>(null);

  const {
    mutate: clone,
    isPending: cloning,
    isSuccess: cloned,
  } = useCloneExperiencesStoryboard(workspaceId ?? "");
  const editorWindowRef = useRef<Window | null>(null);

  const role = draft?.role ?? storyboard?.role;
  const editable = canEdit(role);
  const manageable = canManage(role);

  const storyboardType = storyboard?.type ?? draft?.type;
  const canUnpublish = isInteractiveContent(storyboardType);
  const canEmbed = !isSlideStoryboard(storyboardType);

  const published = Boolean(storyboard?.page?.published);

  const currentDomain = draft?.domain ?? storyboard?.domain;
  const selectedDomain =
    fetchedDomains.find((d) => d.id === currentDomain?.id) ??
    currentDomain ??
    fetchedDomains[0];
  const domainUrl = selectedDomain?.url;
  const canonical = draft?.page?.canonical ?? storyboard?.page?.canonical ?? "";
  const liveUrl =
    (published && domainUrl) ? `${domainUrl}${canonical}` : undefined;
  const pageId = storyboard?.page?.id;
  const storyboardName = storyboard?.name ?? draft?.name ?? "";

  const activeTab = getActiveTab(location.pathname);
  const isLeadProfileRoute = location.pathname.includes("/leads/profile");

  useEffect(() => {
    const appBody = document.getElementById("app-body");
    if (appBody) {
      const prev = appBody.style.scrollBehavior;
      appBody.style.scrollBehavior = "auto";
      appBody.scrollTop = 0;
      appBody.style.scrollBehavior = prev;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (cloned) {
      setCloneName("");
      setCloneError(null);
      setShowClone(false);
    }
  }, [cloned]);

  const handleBack = () => {
    navigate(`/experiences/${workspaceId}/storyboards`);
  };

  const handleProfileBack = () => {
    dispatch(clearSelectedLead());
    navigate(location.pathname.replace(/\/profile.*$/, ""), { replace: true });
  };
  
  const handleTabChange = (tab: StoryboardSettingsTab) => {
    const base = `/experiences/${workspaceId}/storyboards/${storyboardId}`;
    setHideActionBar(false);
    if (isDirty) {
      settings.discard();
    }
    if (tab === "leadsAndReports") {
      navigate(`${base}/reports`);
    // } else if (tab === "rolesPermissions") {
    //   navigate(`${base}/permissions`);
    } else if (tab === "contentManager") {
      navigate(`${base}/content-manager`);
    } else if (tab === "customFields") {
      navigate(`${base}/custom-fields/${storyboardId}`);
    } else {
      navigate(`${base}/website-settings`);
    }
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const handleInvite = () => {
    // if (activeTab === "siteSettings") {
    //   navigate(`/experiences/${workspaceId}/storyboards/${storyboardId}/permissions`, {
    //     state: { openInvite: true },
    //   });
    //   return;
    // }

    if (!workspaceId || !storyboardId) return;
    window.open(
      buildCleverstoryUrl(`/app/${workspaceId}/storyboards/${storyboardId}/permissions`),
      "_blank",
    );
    uploadHandlerRef.current?.();
  };

  const handlePreview = () => {
    if (!workspaceId || !pageId) return;
    window.open(
      buildCleverstoryUrl(buildPagePreviewPath(workspaceId, pageId)),
      "_blank",
    );
  };

  const handleCopyLink = async () => {
    if (!liveUrl) return;
    const ok = await copyToClipboard(liveUrl);
    if (ok) toastSuccess("Copied link to clipboard!");
    else toastFailure("Could not copy link");
  };

  const handleEditSite = () => {
    if (!pageId || !workspaceId) return;
    window.open(
      buildCleverstoryUrl(buildPageTargetPath(workspaceId, pageId)),
      "_blank",
    );
  };

  const handleCode = async () => {
    if (!liveUrl) return;

    const snippet = `
      <div class="cleverstory__live_element" data-live-element="${domainUrl}/embedded/${pageId}" data-id="${pageId}" data-width="100%" data-height="${815}px">
        <div id="page-${pageId}" style="display: none;"></div>
      </div>
      <script>
          !function(){var t=window,e=t.Cleverstory;if("function"==typeof e)e();else{var a=document,n=function(){var t=a.createElement("script");t.type="text/javascript",t.async=!0,t.src="${APPLICATION_URL}/scripts/embed_page.min.js";var e=a.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)};t.attachEvent?t.attachEvent("onload",n):t.addEventListener("load",n,!1)}}();
      </script>
    `;

    const ok = await copyToClipboard(snippet);
    if (ok) toastSuccess("Embed code copied to clipboard!");
    else toastFailure("Could not copy embed code");
  };

  const handleUnpublish = () => setShowUnpublishConfirm(true);

  const handleConfirmUnpublish = () => {
    setUnpublishing(true);
    settings.unpublish({
      onSuccess: () => {
        setUnpublishing(false);
        setShowUnpublishConfirm(false);
        toastSuccess(`${storyboardName} unpublished successfully`);
      },
      onError: (error) => {
        setUnpublishing(false);
        toastFailure(mapServerErrorToToast(error));
      },
    });
  };

  const handleCloseUnpublishConfirm = () => {
    if (unpublishing) return;
    setShowUnpublishConfirm(false);
  };

  const handleDelete = () => {
    if (deleteHandlerRef.current) {
      deleteHandlerRef.current();
    } else {
      setConfirmDelete(true);
    }
  };

  const handleConfirmDelete = () => {
    setDeleting(true);
    settings.deleteStoryboard({
      onSuccess: () => {
        setDeleting(false);
        setConfirmDelete(false);
        toastSuccess(`${storyboardName} deleted successfully`);
        navigate(`/experiences/${workspaceId}/storyboards`);
      },
      onError: (error) => {
        setDeleting(false);
        toastFailure(mapServerErrorToToast(error));
      },
    });
  };

  const handleCloseDeleteConfirm = () => {
    if (deleting) return;
    setConfirmDelete(false);
  };

  const handleClone = () => setShowClone(true);

  const handleCloseClone = () => {
    if (cloning) return;
    setCloneName("");
    setCloneError(null);
    setShowClone(false);
  };

  const handleConfirmClone = () => {
    if (!storyboard?.id || !workspaceId) return;
    const trimmed = cloneName.trim();
    if (!trimmed) {
      setCloneError("Please provide a valid name");
      return;
    }
    if (trimmed.length > 200) {
      setCloneError("Name cannot exceed 200 characters");
      return;
    }

    editorWindowRef.current = window.open("", "_blank");

    clone(
      { id: storyboard.id, name: trimmed },
      {
        onSuccess: (cloned) => {
          const clonedPageId = cloned?.page?.id;
          if (clonedPageId && editorWindowRef.current) {
            editorWindowRef.current.location.href = buildCleverstoryUrl(
              buildPageTargetPath(workspaceId, clonedPageId),
            );
          } else {
            editorWindowRef.current?.close();
          }
          toastSuccess(`${trimmed} cloned successfully`);
          setCloneName("");
          setCloneError(null);
          setShowClone(false);
        },
        onError: (error) => {
          editorWindowRef.current?.close();
          toastFailure(mapServerErrorToToast(error));
        },
      },
    );
  };

  const handleCloneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleConfirmClone();
    if (e.key === "Escape") handleCloseClone();
  };

  const handleSave = () => {
    if (!draft) return;
    const nameError = validateName(draft.name);
    if (nameError) { toastFailure(nameError); return; }
    const aliasError = validateAlias(draft.page?.canonical, draft.type);
    if (aliasError) { toastFailure(aliasError); return; }
    const gatingError = validateGatings(draft.gatingConfigurations);
    if (gatingError) { toastFailure(gatingError); return; }
    settings.save(undefined, {
      onSuccess: () => toastSuccess(`${draft.name} changes saved successfully`),
      onError: (error) => toastFailure(mapServerErrorToToast(error)),
    });
  };


  const shellContextValue = useMemo(
    () => ({ 
      setHideActionBar, 
      registerUploadHandler: (fn: (() => void) | null) => {
        uploadHandlerRef.current = fn;
      },
      registerDeleteHandler: (fn) => { deleteHandlerRef.current = fn; },
      triggerUpload: () => uploadHandlerRef.current?.(),
    }),
    [],
  );

  return (
    <div className={styles.page}>
    {!storyboard ? (
      <div className={styles.loading}>Loading Application...</div>
    ) : (
      <>
      <div className={styles.topBar}>
        <div className={styles.back}>
          <div
            className={styles.item}
            onClick={isLeadProfileRoute ? handleProfileBack : handleBack}
          >
            <i className={cx("paperflite-arrow-up", styles.arrow)} />
            <span className={cx(styles.label, styles.expanded)}>
              my storyboards
            </span>
          </div>
          {activeTab !== "siteSettings" && storyboardName && (
            <>
              <span className={styles.separator}>/</span>
              <TruncateWithTooltip
                text={storyboardName}
                placement="bottom"
                className={styles.storyboardName}
              />
            </>
          )}
        </div>
        {!isLeadProfileRoute && (
        <div className={styles.topBarActions}>
          <SaveConfirmation
            show={isDirty}
            isSaving={settings.isSaving}
            message="Your changes are not saved. Click here to save them."
            onSave={handleSave}
            onCancel={() => settings.discard()}
          />
          <ActionBar
            published={published}
            canEditAccess={editable}
            canManageAccess={manageable}
            isUnpublishing={settings.isUnpublishing}
            storyboardId={storyboardId ?? ""}
            isDeleting={settings.isDeleting}
            hasLiveUrl={Boolean(liveUrl)}
            activeTab={activeTab}
            canPreview={Boolean(pageId)}
            canUnpublish={canUnpublish}
            canEmbed={canEmbed}
            onPreview={handlePreview}
            onCopyLink={handleCopyLink}
            onEditSite={handleEditSite}
            onUnpublish={handleUnpublish}
            onDelete={handleDelete}
            onCode={handleCode}
            onInvite={handleInvite}
            onClone={handleClone}
            show={!isDirty && !hideActionBar}

            />
        </div>
        )}
      </div>
      {!isLeadProfileRoute && (
      <div className={styles.tabsSection}>
        {storyboard && (
          <Tabs
            active={activeTab}
            onChange={handleTabChange}
            manageable={manageable}
            showResourceCenterTabs={
              canEdit(role) &&
              storyboardType === STORYBOARD_TYPES.RESOURCE_CENTER
            }
          />
        )}
      </div>
      )}
      <ShellContext.Provider value={shellContextValue}>
        <div className={styles.panel}>
          <Outlet />
        </div>
      </ShellContext.Provider>

      </>
    )}

      <Slider.Dialog
        width="300"
        show={confirmDelete}
        onClose={handleCloseDeleteConfirm}
      >
        <Slider.Header title="Delete this storyboard?">
          <Text
            className={styles.headerMessage}
            text={`Compilation "${storyboardName}"`}
            lines={3}
            tooltip={{ placement: "auto" }}
          />
        </Slider.Header>
        <Slider.Body>
          <p className={styles.description}>
            If you delete this storyboard, it will be permanently removed along
            with all its settings and content. This cannot be undone. Sure you
            want to continue?
          </p>
          <StoryboardCard storyboard={storyboard as StoryboardItem} workspaceId={workspaceId ?? ""} readOnly={true} />
        </Slider.Body>
        <Slider.Actions>
          <Button
            variant="primary"
            icon="paperflite-tick"
            text={deleting ? "Deleting..." : "Delete"}
            disabled={deleting}
            onClick={handleConfirmDelete}
          />
          <Button
            variant="secondary"
            text="Cancel"
            disabled={deleting}
            onClick={handleCloseDeleteConfirm}
          />
        </Slider.Actions>
      </Slider.Dialog>
      <Slider.Dialog
        width="300"
        show={showUnpublishConfirm}
        onClose={handleCloseUnpublishConfirm}
      >
        <Slider.Header title="Unpublish this storyboard?">
          <Text
            className={styles.headerMessage}
            text={`Compilation "${storyboardName}"`}
            lines={3}
            tooltip={{ placement: "auto" }}
          />
        </Slider.Header>
        <Slider.Body>
          <p className={styles.description}>
            If you unpublish, the current URL will become inactive and lead to a 404 page. You can restore the page anytime from the Storyboard editor. Sure you want to continue?
          </p>
          <StoryboardCard
            storyboard={storyboard as StoryboardItem}
            workspaceId={workspaceId ?? ""}
            readOnly={true}
          />
        </Slider.Body>
        <Slider.Actions>
          <Button
            variant="primary"
            icon="paperflite-tick"
            text={unpublishing ? "Unpublishing..." : "Unpublish"}
            disabled={unpublishing}
            onClick={handleConfirmUnpublish}
          />
          <Button
            variant="secondary"
            text="Cancel"
            disabled={unpublishing}
            onClick={handleCloseUnpublishConfirm}
          />
        </Slider.Actions>
      </Slider.Dialog>
      <Slider.Dialog
        width="300"
        show={showClone}
        onClose={handleCloseClone}
      >
        <Slider.Header title="cloning storyboard">
          <Text
            className={styles.headerMessage}
            text={`Compilation "${storyboardName}"`}
            lines={3}
            tooltip={{ placement: "auto" }}
          />
        </Slider.Header>
        <Slider.Body>
          <StoryboardCard
            storyboard={storyboard as StoryboardItem}
            workspaceId={workspaceId ?? ""}
            readOnly={true}
          />
          <Input
            autoFocus
            className={styles.cloneInput}
            placeholder="Add a storyboard name..."
            value={cloneName}
            onChange={(e) => {
              setCloneName(e.target.value);
              setCloneError(null);
            }}
            onKeyDown={handleCloneKeyDown}
            disabled={cloning}
            maxLength={200}
          />
          <Reveal show={!!cloneError}>
            <div className={styles.cloneError}>
              <i className="fa-solid fa-triangle-exclamation" />
              {cloneError}
            </div>
          </Reveal>
          <p className={styles.cloneDescription}>
            Cloning will create an exact copy of this collection so you can make
            finer edits and start sharing right away.
          </p>
        </Slider.Body>
        <Slider.Actions>
          <Button
            variant="primary"
            icon="paperflite-tick"
            text={cloning ? "Cloning..." : "Confirm"}
            disabled={cloning}
            onClick={handleConfirmClone}
          />
          <Button
            variant="secondary"
            text="Cancel"
            disabled={cloning}
            onClick={handleCloseClone}
          />
        </Slider.Actions>
      </Slider.Dialog>
    </div>
  );
};

export const CleverStoryStoryboardSettingsPage: React.FC = () => {
  const { storyboardId } = useParams();
  const settings = useWebsiteSettings({ storyboardId: storyboardId ?? "" });
  const storyboard = useSelector(selectStoryboard);
  const loading = useSelector(selectStoryboardLoading);

  return loading && !storyboard ? (
    <SiteSettingsSkeleton />
  ) : (
    <SiteSettings actions={settings} storyboardId={storyboardId ?? ""} />
  );
};

export default StoryboardShellLayout;
